/**
 * Shared table chrome for the Attendance tabs, mirroring the Alert Investigation page's
 * data-table look: a rounded bordered card, an uppercase muted header row, hover-highlighted
 * body rows, a chevron pager, and matching loading / empty states. Keeping these in one place
 * means Presence / Payroll / Employees stay visually identical.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  ChevronLeftIcon, ChevronRightIcon, ChevronDownIcon, ChevronUpIcon, ChevronUpDownIcon,
  UserPlusIcon, MagnifyingGlassIcon, XMarkIcon, ExclamationTriangleIcon, ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { Avatar } from '../../components/alerts/Avatar';
import StatusTag from '../../components/common/StatusTag';

/**
 * Shared focus ring. Every interactive control in these tables uses it so keyboard
 * navigation is visible everywhere; `focus-visible` keeps it off mouse clicks.
 */
export const FOCUS_RING = 'outline-none focus-visible:ring-2 focus-visible:ring-brand-ring focus-visible:ring-offset-1 focus-visible:ring-offset-card';

/** Height every filter-bar control shares, so they line up on one baseline. */
const CTRL_H = 'h-8';

/** Default rows shown per page across the Attendance tabs. */
export const PAGE_SIZE = 25;

/**
 * Default for the attendance record tables — Presence, Timeline, Employees and
 * Payroll. These are read as a roster rather than a top-N list (who is in
 * today, everyone's month), so they open wide enough to scan a whole team
 * without paging. Kept here so the four stay in step; readers can still narrow
 * it with <PageSizeSelect>.
 */
export const RECORD_PAGE_SIZE = 50;

/** Page-size choices offered by <PageSizeSelect>. */
export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;

/**
 * Client-side pagination over an in-memory list. Slices `items` into pages, clamps the
 * current page when the dataset shrinks (e.g. after a filter), and exposes ready-to-spread
 * props for <Pager> and <PageSizeSelect>. Callers page over the returned `pageItems`.
 *
 * The page size lives here rather than in each tab so the reader's choice, the slice, and
 * the pager's "N of M" line can never disagree.
 */
export function usePagination<T>(items: T[], initialPageSize = PAGE_SIZE) {
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [page, setPage] = useState(1);
  const total = items.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  // Keep the page valid when the list changes size beneath us.
  useEffect(() => { setPage((p) => Math.min(Math.max(1, p), totalPages)); }, [totalPages]);

  const pageItems = items.slice((page - 1) * pageSize, page * pageSize);
  return {
    page,
    setPage,
    pageSize,
    setPageSize,
    pageItems,
    /** Props to spread directly onto <Pager {...pagerProps} />. */
    pagerProps: {
      page,
      totalPages,
      total,
      pageSize,
      from: total === 0 ? 0 : (page - 1) * pageSize + 1,
      to: Math.min(page * pageSize, total),
      onPrev: () => setPage((p) => Math.max(1, p - 1)),
      onNext: () => setPage((p) => Math.min(totalPages, p + 1)),
    },
    /** Props to spread onto <PageSizeSelect {...pageSizeProps} />. */
    pageSizeProps: {
      value: pageSize,
      // Back to the first page — after resizing, "page 4" refers to different rows.
      onChange: (n: number) => { setPageSize(n); setPage(1); },
    },
  };
}

/**
 * Rows-per-page picker for a tab's filter bar.
 *
 * Deliberately NOT wrapped in a <label>: a <label> around a native <select> re-dispatches
 * the click to the control in some Chromium builds, which opens the dropdown and closes it
 * again so the choice never lands. Uses a plain <span> + aria-label instead.
 *
 * `value` is stringified for the select. React matches a controlled select's value against
 * each option's *string* value, so a numeric value + numeric options relies on coercion;
 * being explicit removes any chance of the control snapping back to the old selection.
 */
export function PageSizeSelect({
  value,
  onChange,
  options = PAGE_SIZE_OPTIONS as readonly number[],
}: {
  value: number;
  onChange: (n: number) => void;
  options?: readonly number[];
}) {
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
      <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Show</span>
      <select
        value={String(value)}
        onChange={(e) => onChange(Number(e.target.value))}
        className={`${CTRL_H} rounded-lg border border-border bg-background px-2 text-[13px] text-foreground transition-colors hover:border-muted-foreground/40 ${FOCUS_RING}`}
        aria-label="Rows per page"
      >
        {options.map((n) => <option key={n} value={String(n)}>{n}</option>)}
      </select>
    </span>
  );
}

// Cell padding tightens below `sm`. These tables are wide enough to scroll horizontally on
// a phone; reclaiming 8px per column is the difference between two visible columns and
// three.
/** Header cell — uppercase, muted, tracked. Reference: AlertInvestigationPage table head. */
export const TH = 'px-3 py-2.5 sm:px-4 sm:py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap';
/** Body cell. */
export const TD = 'px-3 py-2.5 sm:px-4 sm:py-3 text-foreground';
/** Body row — bottom divider (none on last), hover tint. */
export const ROW = 'border-b border-border last:border-0 hover:bg-muted/30 transition-colors';

// ── Column sorting ──────────────────────────────────────────────────────────
// Comparison semantics live in tableSort.ts; this is the React binding plus the header
// cell. Re-exported here so tabs can pull everything table-related from one module.

import { sortRows, type SortState as SortStateT } from './tableSort';

export { timeKey } from './tableSort';
export type { SortDir, SortState } from './tableSort';

/**
 * Click-to-sort over an in-memory list, sitting between filtering and pagination:
 * `filtered → sorted → paged`, so a sort reorders the whole result set rather than just
 * the visible page.
 *
 * `accessors` maps a column key to the value to order that column by — return a number
 * (epoch ms for times, see `timeKey`) rather than a formatted string, or "9:05" sorts
 * after "10:15". The map is read through a ref, so callers may define it inline without
 * re-sorting on every render.
 *
 * Blank values sort last in BOTH directions. Someone who never clocked in has no "first
 * in"; parking them at the top of an ascending sort would bury the rows actually being
 * looked for.
 *
 * The default is no sort at all: every one of these tables arrives in a deliberate server
 * order (presence board grouping, timeline chronology), and that stays until asked.
 */
export function useSort<T>(
  items: T[],
  accessors: Record<string, (row: T) => unknown>,
  initial: SortStateT = { key: null, dir: 'asc' },
) {
  const [sort, setSort] = useState<SortStateT>(initial);
  const accessorsRef = useRef(accessors);
  accessorsRef.current = accessors;

  const sorted = useMemo(() => {
    const get = sort.key ? accessorsRef.current[sort.key] : null;
    return get ? sortRows(items, get, sort.dir) : items;
  }, [items, sort]);

  /** Re-clicking the active column flips direction; a new column starts ascending. */
  const toggleSort = (key: string) =>
    setSort((s: SortStateT) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));

  return { sorted, sort, setSort, toggleSort };
}

/**
 * Sortable header cell. Renders as a button so it is keyboard-reachable, and always shows
 * an affordance — a dimmed up/down glyph when inactive — so readers can tell which columns
 * can be sorted before clicking one.
 */
export function SortableTH({ label, columnKey, sort, onSort, className = TH, title }: {
  label: ReactNode;
  columnKey: string;
  sort: SortStateT;
  onSort: (key: string) => void;
  /** Override for tables with their own header metrics (e.g. Payroll's tighter cells). */
  className?: string;
  title?: string;
}) {
  const active = sort.key === columnKey;
  return (
    // aria-sort belongs on the header CELL, not on the button inside it — that is where
    // assistive tech looks for a column's sort state.
    <th className={className} title={title} scope="col" aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button
        type="button"
        onClick={() => onSort(columnKey)}
        // The active column reads in the brand colour rather than plain foreground: against a
        // header row that is already uppercase and bold, "slightly darker" was not a legible
        // difference, and which column a table is ordered by is the first thing you check
        // when the rows aren't in the order you expected.
        className={`inline-flex items-center gap-1 rounded uppercase tracking-wider transition-colors hover:text-foreground ${active ? 'text-brand' : ''} ${FOCUS_RING}`}
      >
        {label}
        {active
          ? (sort.dir === 'asc' ? <ChevronUpIcon className="h-3 w-3 shrink-0" /> : <ChevronDownIcon className="h-3 w-3 shrink-0" />)
          : <ChevronUpDownIcon className="h-3 w-3 shrink-0 opacity-40" />}
      </button>
    </th>
  );
}

/** Rounded, bordered, elevated table card with horizontal overflow. */
export function TableCard({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-xl border border-border bg-card shadow-card overflow-hidden">
      <div className="overflow-x-auto">{children}</div>
    </div>
  );
}

/**
 * Pulsing skeleton rows shown while the table loads.
 *
 * `aria-busy` + a polite live region: without it a screen reader hears nothing between
 * "table" and the rows appearing, and cannot tell a slow load from an empty one.
 */
export function TableSkeleton({ rows = 6 }: { rows?: number }) {
  return (
    <div className="p-4 space-y-3" aria-busy="true" aria-live="polite" aria-label="Loading">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="h-11 rounded-lg bg-muted animate-pulse" />
      ))}
    </div>
  );
}

/** Centered empty-state message inside the table card. */
export function TableEmpty({ children }: { children: ReactNode }) {
  return <div className="py-16 px-4 text-center text-muted-foreground text-sm">{children}</div>;
}

/**
 * Failed-load state.
 *
 * Exists because every one of these tabs used to swallow a fetch error into console.error
 * and then render the empty state — so "the request failed" and "nobody clocked in today"
 * looked identical, and the operator's reasonable conclusion was the wrong one. A visible,
 * retryable error is the difference between a wrong answer and no answer.
 */
export function TableError({ message, onRetry, children }: {
  /** The underlying failure, shown verbatim beneath the heading when present. */
  message?: string;
  onRetry?: () => void;
  /** Overrides the default heading — say what could not be loaded. */
  children?: ReactNode;
}) {
  return (
    <div className="py-14 px-4 text-center" role="alert">
      <ExclamationTriangleIcon className="mx-auto h-8 w-8" style={{ color: 'var(--destructive)' }} />
      <p className="mt-3 text-sm font-medium text-foreground">{children || "Couldn't load this data"}</p>
      {message && <p className="mt-1 text-xs text-muted-foreground break-words">{message}</p>}
      {onRetry && (
        <button
          onClick={onRetry}
          className={`mt-4 inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-muted ${FOCUS_RING}`}
        >
          <ArrowPathIcon className="h-3.5 w-3.5" /> Try again
        </button>
      )}
    </div>
  );
}

/**
 * Filter-bar search field. Presence, Timeline and Payroll each carried their own copy of
 * this markup; sharing it is what lets Employees, Camera Config and Unknown Faces have one
 * at all, and keeps the clear button and focus ring identical everywhere.
 */
export function SearchInput({ value, onChange, placeholder = 'Search…', label, className = 'w-56' }: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  /** Accessible name; defaults to the placeholder. */
  label?: string;
  /** Width utility — narrow filter bars can shrink it. */
  className?: string;
}) {
  return (
    <div className={`relative ${className}`}>
      <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={label || placeholder}
        // `appearance-none` kills WebKit's own round search decorations, which fight the
        // clear button below and give the field a different silhouette to its neighbours.
        className={`w-full appearance-none rounded-lg border border-border bg-background pl-8 pr-7 text-[13px] text-foreground transition-colors placeholder:text-muted-foreground/70 hover:border-muted-foreground/40 ${CTRL_H} ${FOCUS_RING} [&::-webkit-search-cancel-button]:hidden`}
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange('')}
          aria-label="Clear search"
          className={`absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground transition-colors hover:text-foreground ${FOCUS_RING}`}
        >
          <XMarkIcon className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

interface PagerProps {
  page: number;
  totalPages: number;
  /** Total row count (for the "N total" summary). */
  total: number;
  /** Rows-per-page the slice used. Echoed in the summary; omit to hide. */
  pageSize?: number;
  /** 1-based index of the first row on this page. */
  from: number;
  /** 1-based index of the last row on this page. */
  to: number;
  onPrev: () => void;
  onNext: () => void;
}

/** Bottom pager bar (sits inside the TableCard, below the scroll area). */
export function Pager({ page, totalPages, total, pageSize, from, to, onPrev, onNext }: PagerProps) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border px-4 py-3">
      <span className="text-[12px] text-muted-foreground">
        {from}–{to} of {total} · Page {page} of {totalPages}
        {/* Echoing the size the slice actually used makes a mismatch with the filter-bar
            dropdown immediately visible instead of something you have to count rows for. */}
        {pageSize != null && <> · {pageSize} per page</>}
      </span>
      <nav className="flex items-center gap-2" aria-label="Pagination">
        <button
          disabled={page <= 1}
          onClick={onPrev}
          aria-label="Previous page"
          className={`flex h-8 items-center gap-1 rounded-lg border border-border px-3 text-[12px] text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-30 ${FOCUS_RING}`}
        >
          <ChevronLeftIcon className="h-3.5 w-3.5" /> Prev
        </button>
        <button
          disabled={page >= totalPages}
          onClick={onNext}
          aria-label="Next page"
          className={`flex h-8 items-center gap-1 rounded-lg border border-border px-3 text-[12px] text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-30 ${FOCUS_RING}`}
        >
          Next <ChevronRightIcon className="h-3.5 w-3.5" />
        </button>
      </nav>
    </div>
  );
}

/**
 * The person cell every attendance table opens with: initials bubble, name, and one muted
 * line of context under it.
 *
 * One cell rather than the Name / Code / Department columns it replaces. Those three are
 * read as a single answer to "who is this?", and as separate columns they pushed the data
 * an operator actually came for — is he in, how long has she been here — off the right edge
 * of a laptop screen. The bubble is the same one the alert list uses for assignees, so a
 * person keeps one colour across the whole product.
 */
export function PersonCell({ name, code, detail, badge, onClick }: {
  name?: string | null;
  /** Employee code, shown first on the sub-line. */
  code?: string | null;
  /** What follows the code — department on the presence board, designation in the directory. */
  detail?: string | null;
  /** Trailing chip beside the name, for a state that changes how the row should be read. */
  badge?: ReactNode;
  onClick?: () => void;
}) {
  const sub = [code, detail].filter(Boolean).join(' · ');
  const body = (
    <>
      <Avatar name={name} className="h-9 w-9 text-[11px]" />
      <span className="min-w-0">
        <span className="flex items-center gap-2">
          <span className="truncate font-semibold text-foreground">{name || 'Unknown'}</span>
          {badge}
        </span>
        {sub && <span className="block truncate text-[12px] text-muted-foreground">{sub}</span>}
      </span>
    </>
  );
  if (!onClick) return <span className="flex items-center gap-3">{body}</span>;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center gap-3 rounded text-left ${FOCUS_RING}`}
    >
      {body}
    </button>
  );
}

/**
 * Time-on-site as a meter plus the figure, for the presence board's last column.
 *
 * A bare "5h 41m" is a number you have to hold two other numbers against to judge — the
 * shift length, and what everyone else did. The bar answers both at a glance: a full day is
 * a full bar, and a column of them sorts itself visually. It amber-shades below the half-day
 * threshold, which is the line the payroll classification actually turns on.
 */
export function TimeInsideBar({ minutes, fullDayMinutes = 480, halfDayMinutes }: {
  minutes?: number | null;
  /** Denominator — a full working day. */
  fullDayMinutes?: number;
  /** Below this the bar reads as short; defaults to half of a full day. */
  halfDayMinutes?: number;
}) {
  const mins = Math.max(0, minutes || 0);
  const full = fullDayMinutes > 0 ? fullDayMinutes : 480;
  const half = halfDayMinutes ?? full / 2;
  // Overtime clamps the BAR at full but not the label — the figure beside it still reads
  // 9h 40m, so a long day is visible rather than quietly capped.
  const pct = Math.min(100, (mins / full) * 100);
  const tone = mins === 0 ? null : mins < half ? 'var(--warning)' : 'var(--brand)';
  return (
    <span className="flex items-center gap-2.5">
      <span
        className="h-1.5 w-full min-w-[3rem] max-w-[12rem] overflow-hidden rounded-full bg-muted"
        role="img"
        aria-label={`${fmtDurationShort(mins)} of a ${fmtDurationShort(full)} day`}
        title={`${fmtDurationShort(mins)} of a ${fmtDurationShort(full)} day`}
      >
        {tone && <span className="block h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: tone }} />}
      </span>
      <span className="whitespace-nowrap font-medium tabular-nums text-foreground">{fmtDurationShort(mins)}</span>
    </span>
  );
}

function fmtDurationShort(minutes: number) {
  return `${Math.floor(minutes / 60)}h ${Math.round(minutes % 60)}m`;
}

/**
 * Enrollment photos as a row of pips — how many of the target set are on file.
 *
 * The count is the actionable half of enrollment: "not enrolled" with 0 photos is somebody's
 * job to go and photograph, "not enrolled" with 5 is a job for whoever owns the AI module.
 * The old badge collapsed those two into one word.
 */
export function PhotoPips({ count, registered, target = 5 }: {
  count?: number | null;
  /**
   * Colours the pips. Green once the AI module has the vector — three photos that WORKED
   * need no attention — amber for photos sitting unregistered, which is the state that
   * does. Colouring by count alone flagged a perfectly good 3-of-5 enrolment as a problem.
   */
  registered?: boolean;
  target?: number;
}) {
  const n = Math.max(0, Math.min(target, count || 0));
  const tone = n === 0 ? null : registered ? 'var(--success)' : 'var(--warning)';
  const title = n === 0
    ? 'No enrollment photos on file'
    : `${n} of ${target} enrollment photos on file${registered ? '' : ' — not yet registered'}`;
  return (
    <span className="flex items-center gap-2" title={title}>
      <span className="flex gap-1" aria-hidden="true">
        {Array.from({ length: target }).map((_, i) => (
          <span
            key={i}
            className="h-3.5 w-2.5 rounded-[2px] bg-muted"
            style={i < n && tone ? { backgroundColor: tone } : undefined}
          />
        ))}
      </span>
      <span className="tabular-nums text-[12px] text-muted-foreground">{n}/{target}</span>
    </span>
  );
}

/**
 * Face-enrollment state: a pill over the date it happened.
 *
 * The date is the second half of the answer — an enrolment from before someone changed
 * their appearance is a likely explanation for a face the module keeps missing, and it used
 * to be reachable only by hovering the badge for a tooltip.
 */
export function EnrollmentBadge({ registered, at, photoCount }: {
  registered?: boolean;
  at?: string | null;
  /** Drives the sub-line when not enrolled: no photos = nobody has uploaded any yet. */
  photoCount?: number | null;
}) {
  const when = at ? new Date(at) : null;
  const dated = when && !Number.isNaN(when.getTime());
  return (
    <span className="block">
      <StatusTag tone={registered ? 'success' : 'warning'}>
        {registered ? 'Enrolled' : 'Not enrolled'}
      </StatusTag>
      <span className="mt-0.5 block text-[12px] text-muted-foreground">
        {registered
          ? (dated ? when!.toLocaleDateString([], { day: '2-digit', month: 'short', year: 'numeric' }) : '—')
          : (photoCount ? 'awaiting AI module' : 'awaiting photos')}
      </span>
    </span>
  );
}

/**
 * Brand "Register face" link/button, matching the Alert Investigation face-registration
 * action (soft-brand pill with a user-plus icon).
 */
export function RegisterFaceLink({
  onClick,
  label = 'Register face',
  title = 'Register this face',
}: {
  onClick: () => void;
  label?: string;
  title?: string;
}) {
  return (
    <button
      onClick={onClick}
      title={title}
      // `bg-brand-light` (a pre-mixed tint) rather than `bg-brand/10`: Tailwind v3 drops the
      // opacity modifier on a colour defined as a bare `var()`, which is what --brand is, so
      // the class compiled to nothing and this pill rendered with no fill at all.
      className={`inline-flex h-7 items-center gap-1 rounded-md bg-brand-light px-2 text-[11px] font-medium text-brand transition-colors hover:bg-brand hover:text-white ${FOCUS_RING}`}
    >
      <UserPlusIcon className="h-3.5 w-3.5" /> {label}
    </button>
  );
}

/** Row-level secondary action (Edit) — same metrics as RegisterFaceLink so the two pair up. */
export function RowButton({ onClick, children, title }: { onClick: () => void; children: ReactNode; title?: string }) {
  return (
    <button
      onClick={onClick}
      title={title}
      className={`inline-flex h-7 items-center rounded-md border border-border px-2.5 text-[11px] font-medium text-foreground transition-colors hover:bg-muted ${FOCUS_RING}`}
    >
      {children}
    </button>
  );
}

/**
 * Filter / title bar wrapper — the rounded, bordered, elevated bar that sits above a
 * TableCard and holds a tab's filters, status counts, and actions. Gives Presence /
 * Timeline / Employees an identical "filter title bar" instead of a bare inline row.
 */
export function FilterBar({ children }: { children: ReactNode }) {
  return (
    // gap-y is tighter than gap-x: once this wraps onto several lines on a narrow screen,
    // full 12px vertical gutters push the table below the fold.
    <div className="mb-4 flex flex-wrap items-center gap-x-2 gap-y-2 rounded-xl border border-border bg-card px-3 py-2.5 text-sm shadow-card sm:px-4 sm:py-3">
      {children}
    </div>
  );
}

// ── Filter-bar controls ─────────────────────────────────────────────────────
//
// One height, one radius, one text size for every control in the bar. Before this the
// bars mixed a dozen ad-hoc variants — `py-1` inputs beside `py-0.5` pills beside `text-xs`
// buttons — so nothing shared a baseline and the row read as clutter rather than as a set
// of related controls. Anything placed in a FilterBar should use these.

/** Base styling for a filter-bar `<input>` / `<select>`. */
export const CONTROL = `${CTRL_H} rounded-lg border border-border bg-background px-2.5 text-[13px] text-foreground transition-colors hover:border-muted-foreground/40 ${FOCUS_RING}`;

/** Secondary action (Today, Clear filters, Refresh). */
export const FILTER_BTN = `${CTRL_H} inline-flex items-center gap-1.5 rounded-lg border border-border bg-background px-2.5 text-[13px] font-medium text-foreground transition-colors hover:bg-muted ${FOCUS_RING}`;

/** Primary action (Add employee, Save). */
export const FILTER_BTN_PRIMARY = `${CTRL_H} inline-flex items-center gap-1.5 rounded-lg bg-brand px-3 text-[13px] font-medium text-white transition-colors hover:bg-brand-dark disabled:opacity-50 ${FOCUS_RING}`;

/**
 * Labelled control. The wrapper is a `<span>`, NOT a `<label>` — see PageSizeSelect: a
 * label around a native `<select>` re-dispatches the click in some Chromium builds, so the
 * dropdown opens and immediately closes. Give the control its own `aria-label` (or `title`)
 * so dropping the `for`/`id` association costs nothing.
 */
export function FilterField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{label}</span>
      {children}
    </span>
  );
}

/**
 * Result-count pill. Reads as a readout rather than a control: no border, muted fill, and
 * short enough not to compete with the things that are actually clickable.
 */
export function FilterCount({ children, tone = 'muted' }: { children: ReactNode; tone?: 'muted' | 'accent' }) {
  const styles = tone === 'accent'
    ? { backgroundColor: 'color-mix(in srgb, var(--success) 15%, transparent)', color: 'var(--success)' }
    : undefined;
  return (
    <span
      className={`${CTRL_H} inline-flex items-center rounded-lg px-2.5 text-[12px] font-medium ${tone === 'muted' ? 'bg-muted text-muted-foreground' : ''}`}
      style={styles}
    >
      {children}
    </span>
  );
}

/**
 * Pushes everything after it to the right edge and draws a hairline before it, so the bar
 * splits visibly into "narrow the data" on the left and "act on it" on the right.
 */
export function FilterActions({ children }: { children: ReactNode }) {
  return (
    <span className="ml-auto flex items-center gap-2 border-l border-border pl-2 sm:pl-3">
      {children}
    </span>
  );
}

/** Accent tokens a StatTile can carry. Keys map to the app's theme colours. */
const TILE_TONE = {
  neutral: { value: 'text-foreground', dot: 'bg-muted-foreground/40', ring: 'ring-border' },
  success: { value: 'text-success', dot: 'bg-success', ring: 'ring-success/50' },
  info: { value: 'text-info', dot: 'bg-info', ring: 'ring-info/50' },
  muted: { value: 'text-muted-foreground', dot: 'bg-muted-foreground/50', ring: 'ring-muted-foreground/40' },
} as const;

export type StatTileTone = keyof typeof TILE_TONE;

/**
 * Summary tile for the strip above a table — a big count with a label.
 *
 * With `onClick` it becomes a filter toggle: `active` gives it a coloured ring so the
 * table below is visibly scoped by the tile you picked. Counts passed in should already
 * reflect any other active filters, so the number always equals the rows a click reveals.
 */
export function StatTile({
  label,
  value,
  tone = 'neutral',
  hint,
  active = false,
  onClick,
}: {
  label: string;
  value: number | string;
  tone?: StatTileTone;
  /** Small caption under the label (e.g. what the count means on a past date). */
  hint?: string;
  active?: boolean;
  onClick?: () => void;
}) {
  const t = TILE_TONE[tone];
  const interactive = !!onClick;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!interactive}
      aria-pressed={interactive ? active : undefined}
      className={`rounded-xl border border-border bg-card shadow-card px-4 py-3 text-left transition-colors
        ${interactive ? 'cursor-pointer hover:bg-muted/40' : 'cursor-default'}
        ${active ? `ring-2 ${t.ring}` : ''}`}
    >
      <div className="flex items-center gap-1.5">
        <span className={`h-2 w-2 rounded-full ${t.dot}`} />
        <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</span>
      </div>
      <div className={`mt-1 text-2xl font-semibold tabular-nums ${t.value}`}>{value}</div>
      {hint && <div className="text-[11px] text-muted-foreground">{hint}</div>}
    </button>
  );
}

/** Responsive strip that holds the StatTiles above a FilterBar. */
export function StatTiles({ children }: { children: ReactNode }) {
  return <div className="grid grid-cols-2 gap-3 mb-4 sm:grid-cols-4">{children}</div>;
}

interface SelectOption {
  value: string;
  label: string;
}

/**
 * Searchable single-select dropdown: a trigger button that opens a panel with a filter
 * input and the matching options. Used where a plain <select> gets unwieldy (e.g. linking
 * an unknown face to one of many employees). Closes on outside-click or selection.
 */
export function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = 'Select…',
  searchPlaceholder = 'Search…',
}: {
  options: SelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const selected = options.find((o) => o.value === value);
  const q = query.trim().toLowerCase();
  const filtered = q ? options.filter((o) => o.label.toLowerCase().includes(q)) : options;

  return (
    <div ref={ref} className="relative w-full">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-2 px-2 py-1.5 text-sm rounded border border-border bg-background text-foreground"
      >
        <span className={selected ? 'text-foreground truncate' : 'text-muted-foreground truncate'}>
          {selected ? selected.label : placeholder}
        </span>
        <ChevronDownIcon className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute z-20 mt-1 w-full rounded-lg border border-border bg-card shadow-modal max-h-64 overflow-auto">
          <div className="sticky top-0 bg-card border-b border-border p-2">
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={searchPlaceholder}
              className="w-full px-2 py-1 text-sm rounded border border-border bg-background text-foreground"
            />
          </div>
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-xs text-muted-foreground">No matches</div>
          ) : (
            filtered.map((o) => (
              <button
                type="button"
                key={o.value}
                onClick={() => { onChange(o.value); setQuery(''); setOpen(false); }}
                className={`w-full text-left px-3 py-1.5 text-sm hover:bg-muted transition-colors ${o.value === value ? 'bg-muted/60' : ''}`}
              >
                {o.label}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
